import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowLeft, ArrowRight, ArrowUpRight, Pencil, RotateCcw, Loader2, CheckCircle2,
  X, Image as ImageIcon, AlertCircle, FolderOpen, DatabaseZap, ExternalLink,
} from "lucide-react";
import AdminAuthGate from "@/components/admin/AdminAuthGate";
import { ImageEditModal } from "@/components/cms/CmsImage";
import { resetSlotCaches, resolveUrl } from "@/lib/slotCache";
import {
  IMAGE_SLOT_REGISTRY, groupPage, groupLabel, PAGE_LABELS,
  HOTEL_OPTIONS, hotelKey, isHotelKey, hotelSlugFromKey,
} from "@/lib/imageSlotRegistry";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Tiny helpers ─────────────────────────────────────────────────────────
//
// `stateOf` maps a row to one of five UI states. The "needs-migration"
// state is a NEW category introduced when we started fully declaring
// every homepage image in `imageSlotRegistry.js`: slots whose registry
// entry sets `fallbackKind: "external"` are technically rendering fine
// (the page shows the Unsplash / Pexels / customer-assets URL) but the
// editorial team should replace them with owned Grup Xaluca imagery.
// We surface them as `Needs migration` so they can be filtered and
// worked through without having to grep the codebase.
const stateOf = (row) => {
  if (!row.dbExists) {
    if (row.fallbackKind === "external") return "needs-migration";
    return "default";
  }
  if (row.dbCleared) {
    // A "cleared" slot falls back to the registry fallback URL. If that
    // fallback is external, the same migration nudge applies.
    if (row.fallbackKind === "external") return "needs-migration";
    return "cleared";
  }
  if (row.dbUrl) return "custom";
  return "default";
};
const stateColor = {
  default: "bg-[#E8DBC4] text-[#5C5248]",
  custom:  "bg-[#C16542] text-[#FDFBF7]",
  cleared: "bg-[#2C2621] text-[#FDFBF7]",
  "needs-migration": "bg-[#E9A15A] text-[#2C2621]",
};
const stateLabel = {
  default: "Brand default",
  custom:  "Custom upload",
  cleared: "Reset to default",
  "needs-migration": "Needs migration",
};

// Thumb URL — prefer the db override, then the registry fallback, then
// the runtime-captured brand default (populated by CmsAmbientCapture as
// visitors browse the public site). `revision` is bumped after every
// production sync so cards whose slot URL is unchanged still force the
// browser to refetch the image bytes (the source file on the storage
// bucket may have been replaced).
const thumbUrl = (row, revision = 0, runtimeDefaults = {}) => {
  const registryFallback = row.fallback;
  const runtimeFallback  = runtimeDefaults[row.slot] || null;
  const bestFallback     = registryFallback || runtimeFallback;
  const raw = row.dbCleared
    ? bestFallback
    : (resolveUrl(row.dbUrl) || bestFallback);
  if (!raw) return null;
  if (revision > 0 && (row.dbUrl || row.dbCleared)) {
    return raw.includes("?") ? `${raw}&rev=${revision}` : `${raw}?rev=${revision}`;
  }
  return raw;
};

// ── Preview deep-link ────────────────────────────────────────────────────
//
// Best-effort mapping from a slot to the exact public URL where its
// image is rendered. Also appends `#<section-anchor>` when the slot id
// hints at a known page section (spa-wellness, gallery, rooms…) so the
// admin lands on the right spot instead of the top of the page.
//
// Adds `?cmsPreview=<slot>` so PreviewHighlighter (mounted globally in
// App.js) can scroll the specific <img> into view and pulse it — much
// more helpful than dropping the editor at the top of a 1,300-line
// hotel detail page and asking them to scan for the right photo.
//
// Returns `null` when the page cannot be resolved (unresolved template
// placeholders, auto-detected rows, header/footer-scoped globals).
const SECTION_HASH_BY_SLOT_KEY = {
  "spa-wellness": "spa-wellness",
  "gallery":      "gallery",
  "rooms":        "rooms-services",
  "services":     "rooms-services",
  "excursions":   "excursions",
  "destinations": "nearby",
  "nearby":       "nearby",
  "events":       "events",
  "fb":           "fb",
  "restaurants":  "fb",
  "video":        "video",
  "hero":         "top",
};
const inferSectionHash = (slotId) => {
  if (!slotId) return null;
  const parts = slotId.split(".");
  // Look for the first known section key in the slot's dot path.
  for (const p of parts) {
    if (SECTION_HASH_BY_SLOT_KEY[p]) return SECTION_HASH_BY_SLOT_KEY[p];
  }
  return null;
};

// Whitelist of top-level pages the site actually serves. Used to
// decode unregistered slot ids like `cms.blog.hero.bg` back to
// `/blog` without accidentally routing e.g. `cms.foo.bar` to `/foo`.
const KNOWN_TOP_LEVEL_PAGES = new Set([
  "home", "about", "contact", "booking", "hotels", "bivouacs",
  "experiences", "events", "gastronomy", "trips", "pricing", "packs",
  "blog", "press", "partners", "brochures", "team", "knowledgebase",
  "directions", "wall-of-love", "work-with-us", "southern-morocco",
]);

// Decode a slot id whose registry entry was lost (auto-detected slot)
// back to its most likely public URL. Uses the same conventions
// followed across imageSlotRegistry.js so the mapping stays
// deterministic. Returns `null` when the id doesn't match any known
// pattern.
const decodePageFromSlot = (slot) => {
  if (!slot || typeof slot !== "string") return null;
  // Auto-slot legacy fingerprint. The pathToken (second dot segment)
  // is the original pathname with `/` → `_`. See lib/autoSlot.js.
  if (slot.startsWith("auto.")) {
    const token = slot.split(".")[1] || "";
    if (!token || token === "root") return "/";
    if (token.startsWith("photo-")) return null;   // src-fingerprint, unrecoverable
    return "/" + token.replace(/_/g, "/");
  }
  if (!slot.startsWith("cms.")) {
    // Some legacy slots skip the `cms.` prefix but still use the
    // same dot convention — decode them too.
    // `hotel.<slug>.services.<service>.*` → /hotels/<slug>/services/<service>
    if (slot.startsWith("hotel.")) {
      const p = slot.split(".");
      if (p[2] === "services" && p[1] && p[3]) return `/hotels/${p[1]}/services/${p[3]}`;
      if (p[1]) return `/hotels/${p[1]}`;
    }
    if (slot.startsWith("home.")) return "/";
    if (slot.startsWith("booking")) return "/booking";
    if (slot.startsWith("contact")) return "/contact";
    return null;
  }
  const parts = slot.slice(4).split(".");
  const head  = parts[0];
  // Home page slots — every cms.home.* renders on `/`.
  if (head === "home") return "/";
  // Global brand marks (header/footer logos) — render everywhere,
  // but the home page is the canonical preview surface.
  if (head === "global") return "/";
  // Hotel-scoped slots. `cms.hotel.<slug>.*` → `/hotels/<slug>`.
  if (head === "hotel" && parts[1]) return `/hotels/${parts[1]}`;
  // Hotel video posters use a different scope convention:
  // `cms.hotel-<slug>-videos.video.<video-id>.poster`. Recover the
  // hotel slug by stripping the `hotel-` prefix and `-videos` suffix.
  if (head.startsWith("hotel-") && head.endsWith("-videos")) {
    const slug = head.replace(/^hotel-/, "").replace(/-videos$/, "");
    if (slug) return `/hotels/${slug}`;
  }
  // Hotels-index video posters — grouped by category on `/hotels`:
  // `cms.hotels-videos.video.<category>.poster`.
  if (head === "hotels-videos") return "/hotels";
  // Service pages. `cms.service.<hotel>.<serviceSlug>.*` → real URL.
  if (head === "service" && parts[1] && parts[2]) {
    return `/hotels/${parts[1]}/services/${parts[2]}`;
  }
  // Excursion / nearby cards use `cms.activity.<hotel>.<kind>.<idx>.*`
  // where kind ∈ {excursions, nearby}. Recover the activity URL so
  // editors can view the specific detail page.
  if (head === "activity" && parts[1] && parts[2] && parts[3] !== undefined) {
    const hotel = parts[1];
    const kind  = parts[2];
    const idx   = parts[3];
    if (kind === "excursions" || kind === "nearby") {
      return `/hotels/${hotel}/${kind}/${idx}`;
    }
    return `/hotels/${hotel}`;
  }
  // Bivouacs listing card slots. `cms.bivouac-card.<slug>.*` and
  // `cms.bivouac-*` all render on /bivouacs.
  if (head.startsWith("bivouac")) return "/bivouacs";
  // Experience-scoped: `cms.experience-<key>.*`, `cms.experience-detail.<key>.*`
  // and `cms.experience-related-hotel.*` all render on the detail page.
  if (head === "experience-detail" && parts[1]) {
    return `/experiences/${parts[1]}`;
  }
  if (head.startsWith("experience-")) return "/experiences";
  // Trip / pack cards render on their listing pages.
  if (head === "trip-category") return "/trips";
  if (head === "pack-card")     return "/packs";
  if (head === "pricing-property") return "/pricing";
  if (head === "blog-category")    return "/blog";
  // Heritage / bespoke / partners / press / packs / trip listings —
  // the second-highest match against the whitelist of pages.
  if (KNOWN_TOP_LEVEL_PAGES.has(head)) {
    return head === "home" ? "/" : `/${head}`;
  }
  // Section-hero prefixes used across the site — best guess: the
  // fragment before the first `.` matches a top-level page name.
  return null;
};

const previewUrl = (row) => {
  if (!row || !row.page) return null;
  let page = row.page;
  // Global scope (footer, header, layout) — footer/header render on
  // every page so send the editor to the home page.
  if (page === "(global)")   return `/?cmsPreview=${encodeURIComponent(row.slot)}`;
  // Auto-detected slots — no registry entry. Try to decode the URL
  // from the slot id itself (works for both `auto.*` legacy slots
  // and unregistered `cms.*` slots created by AutoImageEditor).
  if (page === "(other)") {
    const decoded = decodePageFromSlot(row.slot);
    if (!decoded) return null;
    const hash  = inferSectionHash(row.slot);
    const query = `?cmsPreview=${encodeURIComponent(row.slot)}`;
    return hash ? `${decoded}${query}#${hash}` : `${decoded}${query}`;
  }
  // Cross-cutting wildcards — link to the closest listing page.
  if (page === "/hotels/*")  return "/hotels";
  if (page === "/experiences/*") return "/experiences";
  // Template prefixes with unresolved placeholders. Strip the
  // placeholder segment and fall back to the closest concrete parent
  // URL — e.g. `/hotels/<slug>/services/<service>` → `/hotels/<slug>`.
  // This still gives the editor a useful jumping-off point.
  if (page.includes("<") || page.includes("${")) {
    const segments = page.split("/").filter((seg) => !seg.includes("<") && !seg.includes("${"));
    const cleaned = "/" + segments.filter(Boolean).join("/");
    // If nothing usable remains (e.g. the entire path was templated),
    // skip the button rather than emit a broken href.
    if (cleaned === "/" || cleaned.length < 2) return null;
    return cleaned;
  }
  const hash  = inferSectionHash(row.slot);
  const query = `?cmsPreview=${encodeURIComponent(row.slot)}`;
  return hash ? `${page}${query}#${hash}` : `${page}${query}`;
};

// ── Reclassification helpers for unregistered slots ─────────────────────
//
// Iteration 118 — the "(other)" bucket used to swallow every row in
// image_slots whose id wasn't declared in imageSlotRegistry.js. That
// hid ~893 hotel / experience / service edits under one opaque page
// even though their id encoded the exact page they came from.
//
// The helpers below let `allRows` derive the same {page, section,
// hotelSlug, label} tuple for those legacy rows that the registry
// would have carried — so they land in their real hotel + section in
// /admin/images while keeping the `unregistered: true` badge so editors
// can tell them apart from actively-managed slots.

// Section labels — human readable descriptions of the slot's role
// inferred from the slot id path. Falls back to a generic label when
// nothing matches.
const SECTION_LABEL_BY_TOKEN = {
  "spa-wellness":  "Auto-detected · Spa & Wellness",
  "gallery":       "Auto-detected · Gallery",
  "rooms":         "Auto-detected · Rooms & services",
  "services":      "Auto-detected · Rooms & services",
  "excursions":    "Auto-detected · Excursions",
  "destinations":  "Auto-detected · Nearby places",
  "nearby":        "Auto-detected · Nearby places",
  "events":        "Auto-detected · Events",
  "fb":            "Auto-detected · Food & beverage",
  "restaurants":   "Auto-detected · Food & beverage",
  "video":         "Auto-detected · Video posters",
  "videos":        "Auto-detected · Video posters",
  "hero":          "Auto-detected · Hero",
  "activity":      "Auto-detected · Activities",
  "service":       "Auto-detected · Services",
  "instagram":     "Auto-detected · Instagram embed",
  "region":        "Auto-detected · Regions map",
  "trip-category": "Auto-detected · Trip categories",
  "pack-card":     "Auto-detected · Pack cards",
  "blog-category": "Auto-detected · Blog categories",
  "bivouac-card":  "Auto-detected · Bivouac cards",
};
const inferSectionLabel = (slotId) => {
  if (!slotId) return "Auto-detected";
  const parts = slotId.split(".");
  // Search left-to-right — the earliest known token wins so
  // `cms.hotel.<slug>.gallery.image` labels as Gallery even though it
  // starts with `hotel`.
  for (const p of parts) {
    if (SECTION_LABEL_BY_TOKEN[p]) return SECTION_LABEL_BY_TOKEN[p];
  }
  // Legacy path-fingerprint slots. `auto.hotels_<slug>.*` → "Hotel content".
  if (slotId.startsWith("auto.hotels_"))       return "Auto-detected · Hotel content";
  if (slotId.startsWith("auto.experiences_"))  return "Auto-detected · Experience content";
  if (slotId.startsWith("auto.bivouacs"))      return "Auto-detected · Bivouacs content";
  if (slotId.startsWith("auto.root"))          return "Auto-detected · Homepage content";
  return "Auto-detected";
};

// Pull the hotel slug out of a slot id when the id follows a
// hotel-scoped convention. Used to make the row show up under the
// dedicated per-hotel bucket in the sidebar alongside its registered
// siblings.
const inferHotelSlug = (slotId) => {
  if (!slotId) return null;
  // Registered hotel patterns first.
  if (slotId.startsWith("cms.hotel.")) {
    const parts = slotId.split(".");
    return parts[2] || null;
  }
  if (slotId.startsWith("hotel.")) {
    const parts = slotId.split(".");
    return parts[1] || null;
  }
  if (slotId.startsWith("cms.service.") || slotId.startsWith("cms.activity.")) {
    const parts = slotId.split(".");
    return parts[2] || null;
  }
  // Video posters: `cms.hotel-<slug>-videos.*`
  if (slotId.startsWith("cms.hotel-") && slotId.split(".")[0].endsWith("-videos")) {
    const head = slotId.split(".")[0];
    return head.replace(/^cms\.?/, "").replace(/^hotel-/, "").replace(/-videos$/, "") || null;
  }
  // Legacy pathToken fingerprint: `auto.hotels_<slug>...`.
  if (slotId.startsWith("auto.hotels_")) {
    const token = slotId.split(".")[1] || "";
    // Some auto ids nest further: `auto.hotels_<slug>_services_<svc>`.
    // Take everything between `hotels_` and the next `_services_`
    // (or the end of the token if no nesting).
    const stripped = token.replace(/^hotels_/, "");
    const svcIdx = stripped.indexOf("_services_");
    return (svcIdx >= 0 ? stripped.slice(0, svcIdx) : stripped) || null;
  }
  return null;
};

// Build a friendlier label than the raw slot id for the card header.
// Falls back to the slot id itself so power users still see the full
// key when they need to grep across environments.
const inferReadableLabel = (slotId) => {
  if (!slotId) return "";
  // Take the last two segments and humanise them — usually captures
  // the "role" of the image (thumb, hero, card, image, poster, …).
  const parts = slotId.split(".");
  const tail  = parts.slice(-2).join(" · ");
  return `Auto-detected — ${tail}`;
};


// ── Inner page ───────────────────────────────────────────────────────────
const AdminImagesInner = () => {
  const [dbSlots, setDbSlots] = useState({});      // { slot_id → {url, cleared, alt_i18n, source, updated_at} }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [activePage, setActivePage] = useState("all");
  // State filter — narrows the table to a single lifecycle bucket.
  // Value is one of "all" | "custom" | "default" | "needs-migration"
  // | "cleared" | "pending". Kept independent of `activePage` so the
  // editor can combine "Home" + "Needs migration" for focused work.
  const [activeState, setActiveState] = useState("all");
  const [editing, setEditing] = useState(null);     // { slot, currentUrl, currentAlt }

  // Production sync state — driven by the DB PRODUCTION button in the
  // header. Kicks off `POST /api/admin/db/pull` against the safelisted
  // image-related collections (image_slots, media_assets, slot_files,
  // property_galleries, room_galleries) so `/admin/images` mirrors the
  // live site once the pull completes.
  //
  //   status: "idle" | "loading" | "success" | "error"
  //   detail: { collections: [...], syncedAt, error? }
  const [syncStatus, setSyncStatus] = useState("idle");
  const [syncDetail, setSyncDetail] = useState(null);

  // Bumped after every successful production sync. Used as a cache-buster
  // suffix on every rendered thumbnail so the browser refetches images
  // whose URL is unchanged but whose bytes may have been replaced on the
  // origin storage bucket.
  const [imageRevision, setImageRevision] = useState(0);

  // Fetch the full image_slots collection from the public CMS endpoint.
  // We don't need admin auth here because we only render the listing —
  // any mutation happens inside ImageEditModal which already gates with
  // admin password.
  //
  // `bust=true` appends a timestamp query param so intermediate proxies
  // and browser caches never return a stale `/api/slots` payload — this
  // matters right after a production sync, where the payload changes
  // dramatically but the URL and standard cache headers are identical.
  const fetchSlots = useCallback(async ({ bust = false } = {}) => {
    setLoading(true);
    setLoadError("");
    try {
      const url = bust ? `${API}/api/slots?ts=${Date.now()}` : `${API}/api/slots`;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const out = {};
      (data.slots || []).forEach((s) => {
        out[s.slot_id] = s;
      });
      setDbSlots(out);
    } catch (err) {
      setLoadError(err.message || "Failed to load slot directory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // ── Runtime brand-default snapshots ─────────────────────────────────
  //
  // `slot_defaults` collection is populated at runtime by
  // <EditableImage> + <CmsAmbientCapture> whenever the site renders an
  // image using its hard-coded fallback (i.e. no DB override active).
  // For every row on this page whose registry entry left
  // `fallback: null`, we fall back to `runtimeDefaults[row.slot]` so
  // the thumbnail shows the actual brand default instead of "No image
  // yet".
  const [runtimeDefaults, setRuntimeDefaults] = useState({});
  const fetchRuntimeDefaults = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/slot-defaults`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      setRuntimeDefaults(data.defaults || {});
    } catch (_e) {
      // Best-effort — no user-visible error, just fewer thumbnails.
    }
  }, []);
  useEffect(() => { fetchRuntimeDefaults(); }, [fetchRuntimeDefaults]);

  // Pulls image_slots + all image-adjacent collections from the live
  // production DB (visitxaluca.com) into this environment. This is the
  // "make preview match production" one-click sync. The endpoint uses
  // the safelisted cross-env sync pipeline: it fetches, wipes and
  // upserts atomically per collection, then reports per-collection
  // results so partial failures surface clearly to the operator.
  const IMAGE_SYNC_COLLECTIONS = useMemo(() => [
    "image_slots",       // Every Edit-Image-Mode override
    "media_assets",      // Media library entries (URLs, alt text)
    "media_folders",     // Media library folder structure
    "slot_files",        // Uploaded raw files backing image_slots
    "property_galleries",// Per-property section galleries
    "room_galleries",    // Per-room galleries
  ], []);

  const handleSyncFromProduction = useCallback(async () => {
    if (syncStatus === "loading") return;
    setSyncStatus("loading");
    setSyncDetail(null);
    try {
      const res = await fetch(`${API}/api/admin/db/pull`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          collections: IMAGE_SYNC_COLLECTIONS,
          replace: true,
        }),
      });
      if (!res.ok) {
        const raw = await res.text();
        throw new Error(`HTTP ${res.status} · ${raw.slice(0, 240)}`);
      }
      const data = await res.json();
      const results = Array.isArray(data.results) ? data.results : [];
      const failed = results.filter((r) => r.status === "failed");
      if (failed.length) {
        throw new Error(
          `${failed.length}/${results.length} collections failed: ` +
            failed.map((r) => `${r.collection} (${r.reason || "unknown"})`).join(" · ")
        );
      }
      const totalUpserted = results.reduce((acc, r) => acc + (r.upserted || 0), 0);
      const totalRemote = results.reduce((acc, r) => acc + (r.remote_count || 0), 0);
      setSyncDetail({
        sourceUrl: data.source_url,
        syncedAt: data.completed_at,
        results,
        totalUpserted,
        totalRemote,
      });
      // Invalidate the in-memory slot cache so every rendered image
      // picks up the new override on next paint, then refetch the
      // /admin/images listing so the table reflects the fresh state.
      // Pass `bust=true` so the /api/slots fetch bypasses any HTTP
      // cache — right after a sync, an intermediate cache returning
      // the pre-sync payload would make the UI appear unchanged even
      // though the DB is up to date.
      try { resetSlotCaches(); } catch (_e) { /* ignore */ }
      await fetchSlots({ bust: true });
      // Force every rendered <img> to reload — the URL is often unchanged
      // (content-addressable /api/files/... paths) but the bytes behind
      // it may have been replaced on the storage bucket during the
      // sync. Appending ?rev=<n> triggers a fresh browser fetch.
      setImageRevision((n) => n + 1);
      setSyncStatus("success");
      // Scroll back to the header so the operator immediately sees the
      // new stat pills + success banner — otherwise they can be mid-page
      // when the sync completes and miss the confirmation entirely.
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      // Success banner stays visible until manually dismissed — no auto-
      // hide timer. Right after a sync the operator wants to double-check
      // per-collection counts against expectations, and having the banner
      // vanish after 6s is what led editors to think nothing happened.
    } catch (err) {
      setSyncDetail({ error: err.message || "Sync failed" });
      setSyncStatus("error");
    }
  }, [IMAGE_SYNC_COLLECTIONS, fetchSlots, syncStatus]);

  // ── Purge legacy `auto.photo-*` orphan slots ───────────────────────────
  //
  // Iteration 118 — after reclassifying 893 rows into their real
  // page/section, only 3 truly unrecoverable rows remain: id
  // fingerprinted by the image src (not the pathname), 2 of them
  // cleared, 1 pointing at a blob with no metadata. Purging them
  // empties the "Other (unregistered)" bucket entirely.
  //
  //   status: "idle" | "loading" | "success" | "error"
  const [purgeStatus, setPurgeStatus] = useState("idle");
  const [purgeDetail, setPurgeDetail] = useState(null);

  const orphanCount = useMemo(
    () => Object.keys(dbSlots).filter((s) => s.startsWith("auto.photo-")).length,
    [dbSlots]
  );

  const handlePurgeOrphans = useCallback(async () => {
    if (purgeStatus === "loading" || orphanCount === 0) return;
    // Two-step confirmation because the delete is irreversible and
    // hits image_slots directly. window.confirm is enough here — the
    // action is admin-gated and the number of rows is tiny.
    if (typeof window !== "undefined") {
      const msg =
        `Delete ${orphanCount} legacy "auto.photo-*" slot${orphanCount === 1 ? "" : "s"} from the database?\n\n` +
        `These are src-fingerprinted orphans that cannot be classified to a page. ` +
        `No live component reads them (verified by dom_audit.py). This cannot be undone.`;
      if (!window.confirm(msg)) return;
    }
    setPurgeStatus("loading");
    setPurgeDetail(null);
    try {
      const res = await fetch(`${API}/api/admin/slots/purge-photo-orphans`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const raw = await res.text();
        throw new Error(`HTTP ${res.status} · ${raw.slice(0, 240)}`);
      }
      const data = await res.json();
      setPurgeDetail(data);
      setPurgeStatus("success");
      // Refetch so the table drops the deleted rows immediately.
      await fetchSlots({ bust: true });
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      setPurgeDetail({ error: err.message || "Purge failed" });
      setPurgeStatus("error");
    }
  }, [purgeStatus, orphanCount, fetchSlots]);

  // Merge the static registry with DB slots that weren't declared (so
  // nothing falls through the cracks — e.g. slots auto-generated by
  // AutoImageEditor when an editor edited an image directly in the page).
  const allRows = useMemo(() => {
    const declared = new Set(IMAGE_SLOT_REGISTRY.map((r) => r.slot));
    const out = IMAGE_SLOT_REGISTRY.map((entry) => {
      const db = dbSlots[entry.slot];
      return {
        ...entry,
        dbExists: !!db,
        dbUrl: db?.url || null,
        dbCleared: !!db?.cleared,
        dbAlt: db?.alt_i18n || null,
        dbUpdatedAt: db?.updated_at || null,
        pending: !!entry.pending,
      };
    });
    Object.keys(dbSlots).forEach((slotId) => {
      if (declared.has(slotId)) return;
      const db = dbSlots[slotId];
      // Iteration 118 — reclassify legacy / auto-detected slots into
      // their real page + section using the same decoder the "View"
      // deep-link uses. This moves ~893 rows out of the opaque
      // "(other)" bucket and into their proper hotel / homepage /
      // experience / booking dashboards, while keeping the
      // `unregistered: true` flag so they still surface with the
      // orange "Auto-detected" badge that lets editors tell them
      // apart from live registry-managed slots.
      const decodedPage = decodePageFromSlot(slotId);
      const hotelSlug   = inferHotelSlug(slotId);
      const section     = inferSectionLabel(slotId);
      const label       = inferReadableLabel(slotId);
      out.push({
        slot: slotId,
        page: decodedPage || "(other)",
        section,
        label,
        description: decodedPage
          ? "Legacy image edit stored in the DB. Its slot id no longer matches any live component after iteration 115's stable-slot rollout — it's superseded by the modern slot id shown elsewhere on this page. Safe to edit, replace or delete."
          : "Created automatically when an editor edited an image directly on the page. Move into the registry once the surface is known.",
        fallback: null,
        dbExists: true,
        dbUrl: db?.url || null,
        dbCleared: !!db?.cleared,
        dbAlt: db?.alt_i18n || null,
        dbUpdatedAt: db?.updated_at || null,
        pending: false,
        unregistered: true,
        hotelSlug: hotelSlug || undefined,
      });
    });
    return out;
  }, [dbSlots]);

  // Page → row count, used to drive the sidebar.
  const pagesIndex = useMemo(() => {
    const m = {};
    allRows.forEach((r) => {
      const key = groupPage(r.page);
      m[key] = (m[key] || 0) + 1;
    });
    return m;
  }, [allRows]);

  // Hotel → row count, used to drive the per-hotel sidebar block.
  // A slot can belong to a hotel even if it renders on a cross-cutting
  // page (booking thumb, contact thumb, etc.) — `hotelSlug` is derived
  // at registry build time from the slot id.
  const hotelsIndex = useMemo(() => {
    const m = {};
    allRows.forEach((r) => {
      if (r.hotelSlug) m[r.hotelSlug] = (m[r.hotelSlug] || 0) + 1;
    });
    return m;
  }, [allRows]);

  const sortedPages = useMemo(() => {
    // Always list every page declared in PAGE_LABELS — even when it
    // currently has zero slots — so /admin/images is a complete site
    // map. Pages that only render already-managed components surface
    // helpful deep-link rows once clicked (see the per-page placeholder
    // section below). The "/hotels/*" collapsed bucket is excluded
    // because each hotel now gets its own dedicated entry rendered in
    // the "Hotels" sidebar block below.
    const declared = Object.keys(PAGE_LABELS);
    const auto = Object.keys(pagesIndex).filter(
      (k) => !PAGE_LABELS[k] && k !== "/hotels/*"
    );
    return [...declared, ...auto];
  }, [pagesIndex]);

  // Filtered rows for the table.
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (activePage !== "all") {
        if (isHotelKey(activePage)) {
          if (r.hotelSlug !== hotelSlugFromKey(activePage)) return false;
        } else if (groupPage(r.page) !== activePage) {
          return false;
        }
      }
      // State filter — layered on top of the page filter. "pending" is
      // stored on the row itself, everything else derives from
      // stateOf() so it stays in sync with the header pill counts.
      if (activeState !== "all") {
        if (activeState === "pending") {
          if (!(r.pending && !r.dbExists)) return false;
        } else if (r.pending && !r.dbExists) {
          // A pending row is never counted in any other bucket.
          return false;
        } else if (stateOf(r) !== activeState) {
          return false;
        }
      }
      if (!q) return true;
      return (
        r.slot.toLowerCase().includes(q) ||
        (r.label || "").toLowerCase().includes(q) ||
        (r.section || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q)
      );
    });
  }, [allRows, activePage, activeState, query]);

  // Group rows under the selected page by section for readability.
  const grouped = useMemo(() => {
    const m = new Map();
    visibleRows.forEach((r) => {
      const key = r.section || "Other";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    });
    return Array.from(m.entries());
  }, [visibleRows]);

  // Stats for the header pill row.
  //
  // "Needs migration" is a subset of "Brand default" (no DB override
  // yet), but with `fallbackKind: "external"` set in the registry —
  // meaning the fallback URL is on a third-party (Unsplash, Pexels,
  // customer-assets). We count it separately so the editorial team can
  // filter and work through the migration list.
  const stats = useMemo(() => {
    let custom = 0, def = 0, cleared = 0, pending = 0, needsMigration = 0;
    allRows.forEach((r) => {
      if (r.pending && !r.dbExists) pending++;
      else {
        const s = stateOf(r);
        if      (s === "custom")           custom++;
        else if (s === "cleared")          cleared++;
        else if (s === "needs-migration")  needsMigration++;
        else                                def++;
      }
    });
    return { total: allRows.length, custom, default: def, cleared, pending, needsMigration };
  }, [allRows]);

  // When an edit modal closes, refresh the slot we touched.
  const handleEditClose = useCallback(() => {
    setEditing(null);
    // Reset slot caches so the next page navigation sees fresh data.
    resetSlotCaches?.();
    fetchSlots();
  }, [fetchSlots]);

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-images-page">
      {/* Header */}
      <header className="px-6 md:px-10 py-8 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors"
            data-testid="admin-images-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6} />
            Admin hub
          </Link>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="overline text-[#C16542]">Grup Xaluca · Image directory</p>
              <h1 className="font-serif-x text-4xl md:text-5xl mt-2 tracking-tight">
                Every image, in one place
              </h1>
              <p className="mt-3 text-sm text-[#5C5248] max-w-2xl">
                Search, preview and replace any image rendered on the website — grouped by page and section. Edits sync live across EN / FR / ES. Galleries, hotel covers, room photos and pricing-page imagery stay in their dedicated dashboards (linked from each row).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSyncFromProduction}
                disabled={syncStatus === "loading"}
                title="Mirror image_slots, media, galleries from visitxaluca.com into this environment"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors border ${
                  syncStatus === "success"
                    ? "border-[#3F6E4A] bg-[#3F6E4A] text-[#FDFBF7]"
                    : syncStatus === "error"
                    ? "border-[#C16542] bg-[#C16542]/10 text-[#C16542] hover:bg-[#C16542]/15"
                    : syncStatus === "loading"
                    ? "border-[#2C2621]/30 bg-[#2C2621]/5 text-[#2C2621] cursor-wait"
                    : "border-[#2C2621] bg-[#2C2621] text-[#FDFBF7] hover:bg-[#1A1513]"
                }`}
                data-testid="admin-images-db-production"
              >
                {syncStatus === "loading" ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
                    Syncing…
                  </>
                ) : syncStatus === "success" ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                    Synced ({syncDetail?.totalUpserted ?? 0} docs)
                  </>
                ) : syncStatus === "error" ? (
                  <>
                    <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.8} />
                    Retry sync
                  </>
                ) : (
                  <>
                    <DatabaseZap className="w-3.5 h-3.5" strokeWidth={1.6} />
                    DB Production
                  </>
                )}
              </button>
              <Link
                to="/admin/galleries"
                className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
                data-testid="admin-images-link-galleries"
              >
                <FolderOpen className="w-3.5 h-3.5" strokeWidth={1.6} />
                Galleries
              </Link>
              <Link
                to="/admin/media"
                className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
                data-testid="admin-images-link-media"
              >
                <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
                Media library
              </Link>
              {orphanCount > 0 && (
                <button
                  type="button"
                  onClick={handlePurgeOrphans}
                  disabled={purgeStatus === "loading"}
                  title={`Delete ${orphanCount} legacy auto.photo-* orphan slot${orphanCount === 1 ? "" : "s"} that cannot be classified to a page`}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors border ${
                    purgeStatus === "loading"
                      ? "border-[#C16542]/30 bg-[#C16542]/5 text-[#C16542]/70 cursor-wait"
                      : "border-[#C16542]/40 text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7]"
                  }`}
                  data-testid="admin-images-purge-orphans"
                >
                  {purgeStatus === "loading" ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
                      Purging…
                    </>
                  ) : (
                    <>
                      <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                      Purge {orphanCount} orphan{orphanCount === 1 ? "" : "s"}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Purge status banner — success confirms the delete, error
              surfaces the reason. Idle & loading stay silent, the
              button carries their state. */}
          {purgeStatus === "success" && purgeDetail && (
            <div
              className="mt-4 flex flex-wrap items-start gap-3 px-4 py-3 border border-[#3F6E4A]/40 bg-[#3F6E4A]/5 text-[#3F6E4A]"
              data-testid="admin-images-purge-banner-success"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] tracking-[0.22em] uppercase font-medium">
                  Deleted {purgeDetail.deleted ?? 0} orphan slot{(purgeDetail.deleted === 1) ? "" : "s"}
                </p>
                <p className="text-[11px] mt-1 opacity-80 break-all">
                  {(purgeDetail.preview || []).map((p) => p.slot_id).join(" · ") || "—"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setPurgeStatus("idle"); setPurgeDetail(null); }}
                className="text-[10px] tracking-[0.22em] uppercase opacity-70 hover:opacity-100"
                data-testid="admin-images-purge-banner-dismiss"
              >
                Dismiss
              </button>
            </div>
          )}
          {purgeStatus === "error" && purgeDetail?.error && (
            <div
              className="mt-4 flex flex-wrap items-start gap-3 px-4 py-3 border border-[#C16542]/40 bg-[#C16542]/5 text-[#C16542]"
              data-testid="admin-images-purge-banner-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] tracking-[0.22em] uppercase font-medium">Purge failed</p>
                <p className="text-[11px] mt-1 opacity-80 break-all">{purgeDetail.error}</p>
              </div>
              <button
                type="button"
                onClick={() => { setPurgeStatus("idle"); setPurgeDetail(null); }}
                className="text-[10px] tracking-[0.22em] uppercase opacity-70 hover:opacity-100"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Sync status banner — surfaces success + error results with
              per-collection detail. Idle & loading suppress the banner
              (the button itself communicates those two states). */}
          {syncStatus === "success" && syncDetail ? (
            <div
              className="mt-4 flex flex-wrap items-start gap-3 px-4 py-3 border border-[#3F6E4A]/40 bg-[#3F6E4A]/5 text-[#3F6E4A]"
              data-testid="admin-images-sync-banner-success"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] tracking-[0.22em] uppercase font-medium">
                  Preview mirrors production
                </p>
                <p className="mt-1 text-[11px] text-[#3F6E4A]/80">
                  {syncDetail.totalUpserted} docs upserted across {syncDetail.results.length} collections
                  {syncDetail.sourceUrl ? ` from ${syncDetail.sourceUrl}` : ""}.
                </p>
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] tracking-[0.15em]">
                  {syncDetail.results.map((r) => (
                    <li key={r.collection} className="tabular-nums" data-testid={`admin-images-sync-row-${r.collection}`}>
                      <span className="uppercase">{r.collection}</span>
                      <span className="ml-1 opacity-70">· {r.upserted}/{r.remote_count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setSyncStatus("idle")}
                className="text-[#3F6E4A]/70 hover:text-[#3F6E4A] p-1"
                aria-label="Dismiss sync banner"
                data-testid="admin-images-sync-dismiss"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            </div>
          ) : null}
          {syncStatus === "error" && syncDetail?.error ? (
            <div
              className="mt-4 flex flex-wrap items-start gap-3 px-4 py-3 border border-[#C16542]/40 bg-[#C16542]/5 text-[#C16542]"
              data-testid="admin-images-sync-banner-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] tracking-[0.22em] uppercase font-medium">Production sync failed</p>
                <p className="mt-1 text-[11px] break-all">{syncDetail.error}</p>
              </div>
              <button
                type="button"
                onClick={() => setSyncStatus("idle")}
                className="text-[#C16542]/70 hover:text-[#C16542] p-1"
                aria-label="Dismiss error banner"
                data-testid="admin-images-sync-dismiss-error"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            </div>
          ) : null}

          {/* Stat pills — click a pill to filter the table by that
              state; click again (or the "All" pill) to clear the
              filter. Keeps the header count + filter interaction in a
              single visual affordance. */}
          <div className="mt-6 flex flex-wrap items-center gap-2 text-[10px] tracking-[0.22em] uppercase">
            <button
              type="button"
              onClick={() => setActiveState("all")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 transition-opacity ${
                activeState === "all" ? "bg-[#2C2621] text-[#FDFBF7]" : "bg-[#F2EBE1] text-[#2C2621] hover:opacity-80"
              }`}
              data-testid="admin-images-stat-total"
              title="Show all slots"
            >
              {stats.total} slots
            </button>
            <button
              type="button"
              onClick={() => setActiveState(activeState === "custom" ? "all" : "custom")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 transition-opacity ${
                activeState === "custom" ? "bg-[#C16542] text-[#FDFBF7] ring-2 ring-offset-2 ring-[#C16542]/50" : "bg-[#C16542] text-[#FDFBF7] hover:opacity-80"
              }`}
              data-testid="admin-images-stat-custom"
              title="Filter: images uploaded through the CMS"
            >
              <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
              {stats.custom} customised
            </button>
            <button
              type="button"
              onClick={() => setActiveState(activeState === "default" ? "all" : "default")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 transition-opacity ${
                activeState === "default" ? "bg-[#E8DBC4] text-[#5C5248] ring-2 ring-offset-2 ring-[#E8DBC4]" : "bg-[#E8DBC4] text-[#5C5248] hover:opacity-80"
              }`}
              data-testid="admin-images-stat-default"
              title="Filter: slots rendering their in-code brand default"
            >
              {stats.default} brand default
            </button>
            <button
              type="button"
              onClick={() => setActiveState(activeState === "needs-migration" ? "all" : "needs-migration")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 transition-opacity ${
                activeState === "needs-migration" ? "bg-[#E9A15A] text-[#2C2621] ring-2 ring-offset-2 ring-[#E9A15A]" : "bg-[#E9A15A] text-[#2C2621] hover:opacity-80"
              }`}
              data-testid="admin-images-stat-needs-migration"
              title="Filter: slots whose fallback still points to a third-party image (Unsplash, Pexels, customer-assets…) — replace with owned Grup Xaluca imagery."
            >
              {stats.needsMigration} needs migration
            </button>
            <button
              type="button"
              onClick={() => setActiveState(activeState === "cleared" ? "all" : "cleared")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 transition-opacity ${
                activeState === "cleared" ? "bg-[#2C2621] text-[#FDFBF7] ring-2 ring-offset-2 ring-[#2C2621]/50" : "bg-[#2C2621] text-[#FDFBF7] hover:opacity-80"
              }`}
              data-testid="admin-images-stat-cleared"
              title="Filter: slots the editor explicitly reset to default"
            >
              <RotateCcw className="w-3 h-3" strokeWidth={2} />
              {stats.cleared} reset
            </button>
            <button
              type="button"
              onClick={() => setActiveState(activeState === "pending" ? "all" : "pending")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 border border-[#2C2621]/30 text-[#5C5248] transition-opacity ${
                activeState === "pending" ? "ring-2 ring-offset-2 ring-[#2C2621]/30" : "hover:opacity-80"
              }`}
              data-testid="admin-images-stat-pending"
              title="Filter: registry entries not yet wired to a live JSX <EditableImage>"
            >
              {stats.pending} pending wire-up
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar — pages + hotels.
            Scroll-locked at large breakpoints so adding more pages (one
            per hotel, sub-pages, future routes) doesn't push the rest
            of the layout. The inner list scrolls; the header stays put. */}
        <aside className="lg:sticky lg:top-6 self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:pr-2" data-testid="admin-images-sidebar">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C5248] mb-3">Pages</p>
          <button
            type="button"
            onClick={() => setActivePage("all")}
            className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${
              activePage === "all" ? "bg-[#2C2621] text-[#FDFBF7]" : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
            }`}
            data-testid="admin-images-page-all"
          >
            <span>All pages</span>
            <span className={`text-[10px] tracking-[0.22em] ${activePage === "all" ? "text-[#D4A373]" : "text-[#5C5248]"}`}>
              {allRows.length}
            </span>
          </button>
          <ul className="mt-2 space-y-1">
            {sortedPages.map((key) => {
              const meta = groupLabel(key);
              const active = activePage === key;
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => setActivePage(key)}
                    className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${
                      active ? "bg-[#2C2621] text-[#FDFBF7]" : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                    }`}
                    data-testid={`admin-images-page-${key.replace(/[/*]/g, "_") || "root"}`}
                  >
                    <span className="truncate">
                      <span className="mr-2" aria-hidden="true">{meta.emoji}</span>
                      {meta.label}
                    </span>
                    <span className={`text-[10px] tracking-[0.22em] shrink-0 ${active ? "text-[#D4A373]" : "text-[#5C5248]"}`}>
                      {pagesIndex[key] || 0}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Per-hotel dashboards — one dedicated bucket per property so
              editors can manage every image that belongs to a specific
              hotel (detail page hero, ecosystem card, booking thumb,
              contact thumb, pricing thumb, services & activities, etc.)
              from a single screen. */}
          <p className="mt-6 text-[10px] tracking-[0.28em] uppercase text-[#5C5248] mb-3" data-testid="admin-images-sidebar-hotels-label">
            Hotels — per property
          </p>
          <ul className="space-y-1">
            {HOTEL_OPTIONS.map((h) => {
              const key = hotelKey(h.slug);
              const active = activePage === key;
              const count = hotelsIndex[h.slug] || 0;
              return (
                <li key={h.slug}>
                  <button
                    type="button"
                    onClick={() => setActivePage(key)}
                    className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${
                      active ? "bg-[#2C2621] text-[#FDFBF7]" : "text-[#2C2621] hover:bg-[#F2EBE1]/60"
                    }`}
                    data-testid={`admin-images-hotel-${h.slug}`}
                    title={`Manage every image tied to ${h.name}`}
                  >
                    <span className="truncate">
                      <span className="mr-2" aria-hidden="true">🛏</span>
                      {h.name}
                    </span>
                    <span className={`text-[10px] tracking-[0.22em] shrink-0 ${active ? "text-[#D4A373]" : "text-[#5C5248]"}`}>
                      {count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* ── Related admin surfaces ────────────────────────────────
              Fase C — Cross-link (not merge) to /admin/galleries so
              editors can jump to the collections whose imagery is
              managed there rather than in /admin/images. Property and
              room galleries live in their own MongoDB collections
              (`property_galleries`, `room_galleries`) with a
              different schema — surfacing them here as a link keeps
              the two panels discoverable without conflating models. */}
          <p className="mt-6 text-[10px] tracking-[0.28em] uppercase text-[#5C5248] mb-3" data-testid="admin-images-sidebar-related-label">
            Managed elsewhere
          </p>
          <Link
            to="/admin/galleries"
            className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm text-[#2C2621] hover:bg-[#F2EBE1]/60 transition-colors border-l-2 border-transparent hover:border-[#C16542]"
            data-testid="admin-images-related-galleries"
            title="Property and room galleries live in their own admin panel."
          >
            <span className="truncate">
              <span className="mr-2" aria-hidden="true">🖼</span>
              Property &amp; room galleries
              <span className="block text-[10px] tracking-[0.05em] text-[#5C5248] mt-0.5 normal-case">
                28 properties · 28 room sets
              </span>
            </span>
            <ArrowUpRight className="w-3.5 h-3.5 text-[#5C5248] shrink-0" strokeWidth={1.6} />
          </Link>
        </aside>

        {/* Main */}
        <main>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5248]" strokeWidth={1.6} />
            <input
              type="search"
              placeholder="Search by slot id, label, section, description…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-white border border-[#2C2621]/15 focus:border-[#C16542] outline-none pl-10 pr-10 py-3 text-sm transition-colors"
              data-testid="admin-images-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5248] hover:text-[#C16542]"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" strokeWidth={1.6} />
              </button>
            )}
          </div>

          {/* Loading / error */}
          {loading && (
            <div className="mt-12 flex items-center gap-3 text-sm text-[#5C5248]" data-testid="admin-images-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading slot directory…
            </div>
          )}
          {loadError && !loading && (
            <div className="mt-12 flex items-center gap-3 text-sm text-[#C16542]" data-testid="admin-images-error">
              <AlertCircle className="w-4 h-4" />
              {loadError}
            </div>
          )}

          {/* Groups */}
          {!loading && !loadError && grouped.length === 0 && (
            <div
              className="mt-12 bg-white border border-[#2C2621]/10 px-6 py-8"
              data-testid="admin-images-empty"
            >
              <p className="font-serif-x text-2xl mb-2">No standalone image slots here.</p>
              <p className="text-sm text-[#5C5248] leading-relaxed max-w-2xl">
                {activePage === "all" && "No image slots match your filters."}
                {activePage !== "all" && isHotelKey(activePage) && (
                  <>This hotel doesn’t have any registered image slots yet. Open the property page in Edit mode to start adding custom imagery, or visit <code className="text-[#C16542]">/admin/galleries</code> to manage cover photos and room galleries.</>
                )}
                {activePage !== "all" && !isHotelKey(activePage) && (
                  <>The “{(PAGE_LABELS[activePage] || groupLabel(activePage)).label}” page only renders components that are already managed from other dashboards (galleries, hotel covers, etc.). Use the search bar to find an image by name, or pick another page in the sidebar.</>
                )}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/admin/galleries"
                  className="inline-flex items-center gap-1.5 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                >
                  Galleries dashboard
                  <ArrowRight className="w-3 h-3" strokeWidth={1.6} />
                </Link>
                <Link
                  to="/admin/hotels"
                  className="inline-flex items-center gap-1.5 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                >
                  Hotel cover photos
                  <ArrowRight className="w-3 h-3" strokeWidth={1.6} />
                </Link>
                <Link
                  to="/admin/media"
                  className="inline-flex items-center gap-1.5 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                >
                  Media library
                  <ArrowRight className="w-3 h-3" strokeWidth={1.6} />
                </Link>
              </div>
            </div>
          )}

          {!loading && !loadError && grouped.length > 0 && (
            <div className="mt-6 space-y-10">
              {grouped.map(([section, rows]) => (
                <section key={section} data-testid={`admin-images-section-${section.replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}`}>
                  <header className="flex items-baseline justify-between gap-3 mb-3">
                    <h2 className="font-serif-x text-2xl tracking-tight">{section}</h2>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                      {rows.length} slot{rows.length === 1 ? "" : "s"}
                    </span>
                  </header>
                  <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {rows.map((row) => {
                      const st = stateOf(row);
                      const url = thumbUrl(row, imageRevision, runtimeDefaults);
                      return (
                        <li
                          key={row.slot}
                          className="group bg-white border border-[#2C2621]/10 hover:border-[#C16542]/60 transition-colors flex flex-col"
                          data-testid={`admin-images-row-${row.slot.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                        >
                          <div className="aspect-[16/10] bg-[#F2EBE1] overflow-hidden relative">
                            {url ? (
                              <img
                                src={url}
                                alt={row.label}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-[#5C5248] text-[10px] tracking-[0.25em] uppercase">
                                No image yet
                              </div>
                            )}
                            <span className={`absolute top-2 left-2 text-[9px] tracking-[0.22em] uppercase px-2 py-1 ${stateColor[st]}`}>
                              {stateLabel[st]}
                            </span>
                            {row.pending && !row.dbExists && (
                              <span className="absolute top-2 right-2 text-[9px] tracking-[0.22em] uppercase px-2 py-1 bg-[#FDFBF7] text-[#5C5248] border border-[#2C2621]/15">
                                Pending wire-up
                              </span>
                            )}
                            {row.unregistered && (
                              <span className="absolute top-2 right-2 text-[9px] tracking-[0.22em] uppercase px-2 py-1 bg-[#FDFBF7] text-[#C16542] border border-[#C16542]/60">
                                Auto-detected
                              </span>
                            )}
                          </div>
                          <div className="p-4 flex-1 flex flex-col">
                            <p className="font-serif-x text-lg leading-tight">{row.label}</p>
                            <p className="mt-1 text-xs text-[#5C5248] line-clamp-2">{row.description}</p>
                            <code className="mt-3 text-[10px] tracking-[0.05em] text-[#5C5248] break-all bg-[#F2EBE1]/50 px-2 py-1 inline-block">
                              {row.slot}
                            </code>
                            {row.relatedAdmin && (
                              <Link
                                to={row.relatedAdmin.href}
                                className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#C16542] hover:gap-2 transition-all"
                              >
                                {row.relatedAdmin.label}
                                <ArrowRight className="w-3 h-3" strokeWidth={1.6} />
                              </Link>
                            )}
                            <div className="mt-auto pt-4 flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditing({
                                    slot: row.slot,
                                    currentUrl: thumbUrl(row, imageRevision, runtimeDefaults),
                                    currentAlt: "",
                                    currentAltI18n: row.dbAlt || null,
                                  })}
                                  disabled={row.pending && !row.dbExists}
                                  className="inline-flex items-center gap-2 bg-[#2C2621] text-[#FDFBF7] hover:bg-[#C16542] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                                  data-testid={`admin-images-edit-${row.slot.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                                  title={row.pending && !row.dbExists ? "This slot will be editable once its component is wired up in fase 2/3 of the migration." : "Replace, upload from library, or reset to brand default"}
                                >
                                  <Pencil className="w-3 h-3" strokeWidth={1.8} />
                                  {row.pending && !row.dbExists ? "Pending" : "Edit"}
                                </button>
                                {(() => {
                                  // Deep-link to the live page where this
                                  // image is rendered, with a section hash
                                  // + ?cmsPreview=<slot> so PreviewHighlighter
                                  // can scroll & pulse the exact <img>.
                                  const href = previewUrl(row);
                                  if (!href) return null;
                                  return (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 border border-[#2C2621]/30 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors"
                                      data-testid={`admin-images-view-${row.slot.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}`}
                                      title={`Open ${row.page} in a new tab and scroll to this image`}
                                    >
                                      <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
                                      View
                                    </a>
                                  );
                                })()}
                              </div>
                              {row.dbUpdatedAt && (
                                <span className="text-[10px] tracking-[0.15em] text-[#5C5248] uppercase">
                                  Updated {row.dbUpdatedAt.slice(0, 10)}
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Edit modal — reuses the same UX as inline page edits */}
      {editing && (
        <ImageEditModal
          slot={editing.slot}
          currentUrl={editing.currentUrl}
          currentAlt={editing.currentAlt}
          currentAltI18n={editing.currentAltI18n}
          onClose={handleEditClose}
        />
      )}
    </section>
  );
};

const AdminImagesPage = () => (
  <AdminAuthGate>
    <AdminImagesInner />
  </AdminAuthGate>
);

export default AdminImagesPage;
